import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, MenuItem, Box, Typography, CircularProgress, Alert,
} from "@mui/material";
import { PersonAddOutlined } from "@mui/icons-material";
import { fetchLeadPrefill, createLead, LeadPrefill, PRIORITY_OPTIONS } from "./emailHubV3Api";

const EVENT_TYPES = ["wedding", "corporate", "birthday", "anniversary", "gala", "other"];

interface Props {
  open: boolean;
  onClose: () => void;
  threadId: number;
  onCreated: (lead: any) => void;
}

const CreateLeadModal: React.FC<Props> = ({ open, onClose, threadId, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<Record<string, any>>({
    customer_name: "", email: "", phone: "", organization: "",
    event_type: "", event_date: "", guest_count: "", budget: "",
    preferred_hall: "", notes: "", priority: "medium",
  });

  useEffect(() => {
    if (open && threadId) {
      setLoading(true);
      setError("");
      fetchLeadPrefill(threadId)
        .then((data) => {
          setForm((prev) => ({
            ...prev,
            customer_name: data.customer_name || "",
            email: data.email || "",
            phone: data.phone || "",
            event_type: data.event_type || "",
            event_date: data.event_date || "",
            guest_count: data.guest_count ?? "",
            budget: data.budget || "",
            preferred_hall: data.preferred_hall || "",
            notes: data.notes || "",
          }));
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, threadId]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.customer_name.trim()) { setError("Customer name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const lead = await createLead({ ...form, thread_id: threadId });
      onCreated(lead);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to create lead.");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
        <PersonAddOutlined sx={{ color: "#3b82f6" }} /> Create Lead from Thread
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
              AI-extracted data has been pre-filled. Adjust as needed.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Customer Name" value={form.customer_name}
                  onChange={handleChange("customer_name")} fullWidth required size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Email" value={form.email}
                  onChange={handleChange("email")} fullWidth size="small" type="email" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Phone" value={form.phone}
                  onChange={handleChange("phone")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Organization" value={form.organization}
                  onChange={handleChange("organization")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Event Type" value={form.event_type}
                  onChange={handleChange("event_type")} fullWidth size="small" select>
                  <MenuItem value="">—</MenuItem>
                  {EVENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Event Date" value={form.event_date}
                  onChange={handleChange("event_date")} fullWidth size="small"
                  type="date" InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Guest Count" value={form.guest_count}
                  onChange={handleChange("guest_count")} fullWidth size="small" type="number" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Budget" value={form.budget}
                  onChange={handleChange("budget")} fullWidth size="small" type="number" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Preferred Hall" value={form.preferred_hall}
                  onChange={handleChange("preferred_hall")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Priority" value={form.priority}
                  onChange={handleChange("priority")} fullWidth size="small" select>
                  {PRIORITY_OPTIONS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Notes" value={form.notes}
                  onChange={handleChange("notes")} fullWidth size="small"
                  multiline rows={3} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : <PersonAddOutlined />}
          sx={{ background: "#3b82f6", "&:hover": { background: "#2563eb" } }}>
          Create Lead
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLeadModal;
